import { useEffect, useRef, useState } from "react";
import { Popup } from "@/components/common/Popup";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useTranslate } from "@/helper/translate";
import { Loader2 } from "lucide-react";

import { Checkbox } from "../ui/checkbox";
import { useNavigate } from "react-router-dom";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  eventId: string;
  onError?: (message: string) => void;
};

// ─── main popup ──────────────────────────────────────────────────────────────

export function DeleteDataPopup({  isOpen, onClose, apiFetch, eventId, onError  }: Props) {
 
const [deleteData, setDeleteData] = useState<any>(null);
const [deleteAll, setDeleteAll] = useState<boolean>(false);
const [message, setMessage] = useState<string>("");
const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const navigate = useNavigate();
  const { translate } = useTranslate();

const deleteDataHandler = () => {
  if (isDeleting) return;
  setIsDeleting(true);
 const payload = {
          deleteAll,
          deleteVods: deleteData?.vods?.filter((v: any) => v.delete).map((v: any) => v.Id) ?? [],
          deleteQandA: deleteData?.qanda?.filter((q: any) => q.delete).map((q: any) => q.Id) ?? [],
          deleteTracking: deleteData?.plenums?.filter((p: any) => p.delete).map((p: any) => p.Id) ?? [],
          deleteChats: deleteData?.chats?.filter((c: any) => c.delete).map((c: any) => c.Id) ?? [],
 };

 apiFetch(`/admin/DeleteEventData?eventId=${eventId}&data=${JSON.stringify(payload)}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        }
        
      })
        .then((res) => {

          if (!res.ok) 
            {
              var errorMessage = "Failed to delete data";
              res.text().then((text) => {
                if (text) errorMessage += `: ${text}`;
                onError?.(errorMessage);
              });
            }
            else
              {
                if(deleteAll)
                  navigate(`/events`);
                else
                  {
                  setMessage(translate("ui.datadeleted"));
                  }

              }
         
        })
        
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setIsDeleting(false);
        });
}


  useEffect(() => {
   
     if(!isOpen) return;

      apiFetch(`/admin/GetDataToDelete?eventId=${eventId}`, {
        method: "GET",
      })
        .then((res) => {
          if (!res.ok) throw new Error("Failed to fetch events");
          return res.json();
        })
        .then((data) => {
         //plenums, chats, vods, qanda 
        setDeleteData(data);
          
        })
        .catch((err) => {
          console.error(err);
        });
    

  }, [ eventId, isOpen]);



  return (
    <Popup
      isOpen={isOpen}
      onClose={onClose}
      title={translate("ui.deleteEventData")}
      className="w-[95vw] max-w-5xl"
    >
      
      <div className="flex flex-col gap-4 min-h-[400px]">

       <div>{translate('ui.whatInformationDoYouWantToDelete')}</div>
       <div className="text-red-500"> <Checkbox
            checked={deleteAll}
            label={translate('ui.deleteEntireEvent')}
            onCheckedChange={(checked) => {
              setDeleteAll(checked);
            }}
          /></div>

      
       {!deleteAll && deleteData && deleteData.vods != null && deleteData.vods.length > 0 && 
       deleteData.vods.map((vod: any) => (
        <div key={vod.Id} className="flex items-center gap-2">
          <Checkbox
            checked={vod.delete}
            label={<>VOD: {deleteData.plenums.find((s: any) => s.Id == vod.Id)?.Name}</>}
            onCheckedChange={(checked) => {
              const updatedVods = deleteData.vods.map((v: any) =>
                v.Id === vod.Id ? { ...v, delete: checked } : v
              );
              setDeleteData({ ...deleteData, vods: updatedVods });
            }}
          />
        </div>
       ))
      }
       

           {!deleteAll && deleteData && deleteData.qanda != null && deleteData.qanda.length > 0 && 
       deleteData.qanda.map((qanda: any) => (
        <div key={qanda.Id} className="flex items-center gap-2">
          <Checkbox
            checked={qanda.delete}
            label={<>Q & A Fragen: {deleteData.plenums.find((s: any) => s.Id == qanda.PlenumId)?.Name}</>}

            onCheckedChange={(checked) => {
              const updatedQanda = deleteData.qanda.map((q: any) =>
                q.Id === qanda.Id ? { ...q, delete: checked } : q
              );
              setDeleteData({ ...deleteData, qanda: updatedQanda });
            }}
          />
        </div>
       ))
      }
       

 {!deleteAll &&deleteData && deleteData.plenums != null && deleteData.plenums.length > 0 && 
       deleteData.plenums.map((plenum: any) => (
        <div key={plenum.Id} className="flex items-center gap-2">
          <Checkbox
            checked={plenum.delete}
            label={<>User Tracking for  {plenum.Name}</>}
            onCheckedChange={(checked) => {
              const updatedPlenums = deleteData.plenums.map((p: any) =>
                p.Id === plenum.Id ? { ...p, delete: checked } : p
              );
              setDeleteData({ ...deleteData, plenums: updatedPlenums });
            }}
          />
        </div>
       ))
      }


      {!deleteAll &&deleteData && deleteData.chats != null && deleteData.chats.length > 0 && 
       deleteData.chats.map((chat: any) => (
        <div key={chat.Id} className="flex items-center gap-2">
          <Checkbox
            checked={chat.delete}
            label={<>Chat Verlauf: {chat.Name}</>}
            onCheckedChange={(checked) => {
              const updatedChats = deleteData.chats.map((c: any) =>
                c.Id === chat.Id ? { ...c, delete: checked } : c
              );
              setDeleteData({ ...deleteData, chats: updatedChats });
            }}
          />
        </div>
       ))
      }

      <Button onClick={deleteDataHandler} disabled={isDeleting} className="mt-2 max-w-xs">
        {isDeleting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Bitte warten...
          </>
        ) : (
          "Daten löschen"
        )}
      </Button>
      </div>
        {message && <div className="text-green-600 mt-4">{message}</div>}
    </Popup>
  );
}
